/**
 * LinkedIn issues no refresh token: the access token dies after 60 days and
 * the member must run the OAuth flow again. Both the tile and the detail page
 * derive their warning state from the same two integration fields.
 */

/** Warn this many days before the token expires. */
export const LINKEDIN_TOKEN_EXPIRY_WARNING_DAYS = 7;

export type LinkedinTokenState =
  | { kind: 'expired'; expiresAt: Date | null }
  | { kind: 'expiring'; expiresAt: Date; daysLeft: number }
  | { kind: 'ok'; expiresAt: Date | null };

type TokenStateSource = {
  needsReauthorization?: boolean | null;
  tokenExpiresAt?: string | null;
};

export function getLinkedinTokenState(
  integration: TokenStateSource | null | undefined,
  now: Date = new Date(),
): LinkedinTokenState {
  const expiresAt = integration?.tokenExpiresAt
    ? new Date(integration.tokenExpiresAt)
    : null;
  const validExpiresAt =
    expiresAt && !Number.isNaN(expiresAt.getTime()) ? expiresAt : null;

  if (integration?.needsReauthorization) {
    return { kind: 'expired', expiresAt: validExpiresAt };
  }
  if (!validExpiresAt) return { kind: 'ok', expiresAt: null };

  const msLeft = validExpiresAt.getTime() - now.getTime();
  if (msLeft <= 0) return { kind: 'expired', expiresAt: validExpiresAt };

  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
  if (daysLeft <= LINKEDIN_TOKEN_EXPIRY_WARNING_DAYS) {
    return { kind: 'expiring', expiresAt: validExpiresAt, daysLeft };
  }
  return { kind: 'ok', expiresAt: validExpiresAt };
}
